import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebaseConfig';
import { useAuth } from '../AuthContext';
import { useOrg } from '../data/orgs';
import { useToast } from '../components/Toast';
import Icon from '../components/Icons';
import { samSync, samPursue, samDeliverability, isAiConfigured } from '../data/legalApi';

// SAM desk — the federal opportunity pipeline as a product surface.
// The sync worker (backend/api/samWorker.js) pulls + scores SAM.gov notices
// into orgs/{orgId}/opportunities (doc id = NoticeId); this page is the
// Monday-triage meeting: funnel → screens → bid / watch / pass → pursue.
// Triage decisions are plain Firestore writes (the register pattern);
// pursue/deliverability hit the server. Bid screens & scoring rationale live
// in the R14 - SAM project's bid-scope doc and shared/samScreen.js.

const BAND_COLOR = { A: 'var(--ok)', B: 'var(--info)', C: 'var(--muted)', D: 'var(--muted)' };
const VERDICT = {
  yes: { label: 'deliverable', color: 'var(--ok)' },
  stretch: { label: 'stretch', color: '#ffd166' },
  no: { label: 'not ours', color: 'var(--accent, #ff6b6b)' },
};
const LANE_LABEL = {
  rcm: 'RCM', idp: 'IDP', healthOps: 'HealthOps', workflow: 'Workflow',
  cc: 'Contact center', coding: 'Coding', vaBenefits: 'VA benefits',
};

const daysLeft = (ms) => (ms ? Math.floor((ms - Date.now()) / 86400000) : null);
const fmtDate = (ms) => (ms ? new Date(ms).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—');

function DeadlineChip({ ms }) {
  const d = daysLeft(ms);
  if (d === null) return <span className="cs-chip">no deadline</span>;
  const tone = d < 0 ? 'var(--muted)' : d <= 7 ? 'var(--accent, #ff6b6b)' : d <= 21 ? '#ffd166' : 'var(--muted)';
  return (
    <span className="cs-chip" style={{ color: tone }}>
      {fmtDate(ms)}{d >= 0 ? ` · ${d}d` : ' · past'}
    </span>
  );
}

function topLanes(scores = {}) {
  return Object.entries(scores)
    .filter(([k, v]) => LANE_LABEL[k] && v > 0)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3);
}

const TABS = [
  { key: 'shortlist', label: 'Shortlist' },
  { key: 'due', label: 'Due soon' },
  { key: 'bid', label: 'Bid' },
  { key: 'watch', label: 'Watch' },
  { key: 'new', label: 'Untriaged' },
  { key: 'all', label: 'All active' },
];

export default function SamDesk() {
  const { user } = useAuth();
  const { orgId, role } = useOrg();
  const toast = useToast();
  const navigate = useNavigate();
  const [opps, setOpps] = useState(null);
  const [tab, setTab] = useState('shortlist');
  const [q, setQ] = useState('');
  const [openId, setOpenId] = useState(null);
  const [busy, setBusy] = useState('');

  useEffect(() => {
    if (!orgId) return undefined;
    const unsub = db.collection(`orgs/${orgId}/opportunities`)
      .where('active', '==', true)
      .onSnapshot(
        (snap) => setOpps(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
        (err) => { console.warn(err); setOpps([]); }
      );
    return () => unsub();
  }, [orgId]);

  const stats = useMemo(() => {
    const list = opps || [];
    return {
      total: list.length,
      aBand: list.filter((o) => o.fitBand === 'A').length,
      due30: list.filter((o) => { const d = daysLeft(o.responseDeadline); return d !== null && d >= 0 && d <= 30; }).length,
      bid: list.filter((o) => o.triage?.decision === 'bid').length,
      pursuing: list.filter((o) => o.pursuitDealId).length,
    };
  }, [opps]);

  const rows = useMemo(() => {
    let list = (opps || []).filter((o) => o.triage?.decision !== 'pass' || tab === 'all');
    if (tab === 'shortlist') {
      list = list.filter((o) => (o.fitBand === 'A' || o.fitBand === 'B')
        && o.deliverability?.verdict !== 'no'
        && (o.screens?.pass || o.deliverability?.verdict === 'yes'));
    } else if (tab === 'due') {
      list = list.filter((o) => { const d = daysLeft(o.responseDeadline); return d !== null && d >= 0 && d <= 30; });
    } else if (tab === 'bid' || tab === 'watch') {
      list = list.filter((o) => o.triage?.decision === tab);
    } else if (tab === 'new') {
      list = list.filter((o) => !o.triage?.decision);
    }
    if (q.trim()) {
      const needle = q.trim().toLowerCase();
      list = list.filter((o) => `${o.title} ${o.agency} ${o.subTier} ${o.naics} ${o.noticeType}`.toLowerCase().includes(needle));
    }
    return list.sort(tab === 'due'
      ? (a, b) => (a.responseDeadline || Infinity) - (b.responseDeadline || Infinity)
      : (a, b) => (b.fitScore || 0) - (a.fitScore || 0));
  }, [opps, tab, q]);

  const triage = async (opp, decision) => {
    const name = user.displayName || (user.email || '').split('@')[0];
    const prev = opp.triage || {};
    try {
      await db.doc(`orgs/${orgId}/opportunities/${opp.id}`).update({
        triage: {
          ...prev,
          decision,
          byUid: user.uid,
          byName: name,
          at: Date.now(),
          history: [...(prev.history || []), { decision, byName: name, at: Date.now() }].slice(-20),
        },
        updatedAt: Date.now(),
      });
    } catch (err) { toast(err.message, 'danger'); }
  };

  const pursue = async (opp) => {
    setBusy(`pursue-${opp.id}`);
    try {
      const { dealId, existing } = await samPursue({ orgId, oppId: opp.id });
      toast(existing ? 'Opening the existing pursuit' : 'Pursuit project created — notice + attachments ingesting', 'ok');
      navigate(`/deals/${dealId}`);
    } catch (err) { toast(err.message, 'danger'); } finally { setBusy(''); }
  };

  const checkDeliverability = async (opp) => {
    setBusy(`llm-${opp.id}`);
    try {
      await samDeliverability({ orgId, oppId: opp.id });
      toast('Deliverability screened', 'ok');
    } catch (err) { toast(err.message, 'danger'); } finally { setBusy(''); }
  };

  const syncNow = async () => {
    setBusy('sync');
    try {
      const r = await samSync({ orgId });
      if (r.skipped) toast(`Sync skipped: ${r.skipped}`, 'warn');
      else if (r.error) toast(`Sync failed: ${r.error}`, 'danger');
      else toast(`Synced — ${r.created} new, ${r.updated} updated, ${r.screened} screened`, 'ok');
    } catch (err) { toast(err.message, 'danger'); } finally { setBusy(''); }
  };

  if (!orgId) return <div className="cs-page"><p className="cs-help">Pick an organization first.</p></div>;

  return (
    <div className="cs-page">
      <div className="cs-page-head">
        <div>
          <h1>SAM desk</h1>
          <p className="cs-help">
            Federal opportunity pipeline — scored by the fit engine, screened, triaged here, pursued as projects.
          </p>
        </div>
        {(role === 'admin' || role === 'owner') && (
          <button type="button" className="cs-btn cs-btn-outline" disabled={busy === 'sync'} onClick={syncNow}>
            <Icon name="search" size={14} /> {busy === 'sync' ? 'Syncing…' : 'Sync now'}
          </button>
        )}
      </div>

      {/* Funnel */}
      <div style={{ display: 'flex', gap: 'var(--sp-2)', flexWrap: 'wrap', marginBottom: 'var(--sp-4)' }}>
        <span className="cs-chip">{stats.total} active</span>
        <span className="cs-chip" style={{ color: 'var(--ok)' }}>{stats.aBand} A-band</span>
        <span className="cs-chip">{stats.due30} due ≤30d</span>
        <span className="cs-chip" style={{ color: 'var(--info)' }}>{stats.bid} bid</span>
        <span className="cs-chip">{stats.pursuing} pursuing</span>
      </div>

      <div style={{ display: 'flex', gap: 'var(--sp-2)', flexWrap: 'wrap', alignItems: 'center', marginBottom: 'var(--sp-4)' }}>
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            className={`cs-btn cs-btn-sm ${tab === t.key ? 'cs-btn-primary' : 'cs-btn-ghost'}`}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
        <input
          className="cs-input"
          style={{ marginLeft: 'auto', maxWidth: 260 }}
          placeholder="Filter title / agency / NAICS…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      </div>

      {opps === null && <div className="cs-skeleton" style={{ height: 120 }} />}
      {opps !== null && !rows.length && (
        <div className="cs-empty">
          {stats.total === 0
            ? 'No opportunities yet — run a sync (needs the SAM.gov API key in SSM and sam.enabled in live config), or seed from the tracker.'
            : 'Nothing matches this view.'}
        </div>
      )}

      <div style={{ display: 'grid', gap: 'var(--sp-2)' }}>
        {rows.map((o) => {
          const open = openId === o.id;
          const v = o.deliverability ? VERDICT[o.deliverability.verdict] : null;
          const decision = o.triage?.decision;
          return (
            <div key={o.id} className="cs-card" style={{ padding: 'var(--sp-3) var(--sp-4)' }}>
              <div
                style={{ display: 'flex', gap: 'var(--sp-3)', alignItems: 'center', cursor: 'pointer', flexWrap: 'wrap' }}
                onClick={() => setOpenId(open ? null : o.id)}
              >
                <span
                  className="cs-badge"
                  style={{ background: 'transparent', border: `1px solid ${BAND_COLOR[o.fitBand] || 'var(--muted)'}`, color: BAND_COLOR[o.fitBand] || 'var(--muted)', minWidth: 42, textAlign: 'center' }}
                  title={`FitScore ${o.fitScore}`}
                >
                  {o.fitBand || '?'} {o.fitScore ?? ''}
                </span>
                <div style={{ flex: '1 1 320px', minWidth: 240 }}>
                  <div style={{ fontWeight: 600 }}>{o.title}</div>
                  <div className="u-xs u-muted">
                    {o.agency}{o.subTier ? ` · ${o.subTier}` : ''} · {o.noticeType || '—'} · NAICS {o.naics || '—'}
                    {o.setAside ? ` · ${o.setAside}` : ''}
                  </div>
                </div>
                <DeadlineChip ms={o.responseDeadline} />
                {v && <span className="cs-chip" style={{ color: v.color }}>{v.label}</span>}
                {topLanes(o.scores).map(([k, s]) => (
                  <span key={k} className="cs-chip" title={`${LANE_LABEL[k]} score ${s}`}>{LANE_LABEL[k]}</span>
                ))}
                {decision && <span className="cs-chip" style={{ color: decision === 'bid' ? 'var(--info)' : undefined }}>{decision}</span>}
                {o.pursuitDealId && <span className="cs-chip" style={{ color: 'var(--ok)' }}>pursuing</span>}
              </div>

              {open && (
                <div style={{ marginTop: 'var(--sp-3)', display: 'grid', gap: 'var(--sp-3)' }}>
                  {o.screens?.reasons?.length > 0 && (
                    <div className="u-xs u-muted">
                      {o.screens.reasons.map((r, i) => <div key={i}>· {r}</div>)}
                    </div>
                  )}
                  {o.deliverability?.rationale && (
                    <div className="u-xs" style={{ color: v?.color }}>
                      Deliverability ({o.deliverability.verdict}): {o.deliverability.rationale}
                    </div>
                  )}
                  {o.description && (
                    <p className="u-xs u-muted" style={{ maxHeight: 140, overflow: 'auto', whiteSpace: 'pre-wrap', margin: 0 }}>
                      {o.description.slice(0, 1800)}{o.description.length > 1800 ? '…' : ''}
                    </p>
                  )}
                  <div style={{ display: 'flex', gap: 'var(--sp-2)', flexWrap: 'wrap' }}>
                    {['bid', 'watch', 'pass'].map((d) => (
                      <button
                        key={d}
                        type="button"
                        className={`cs-btn cs-btn-sm ${decision === d ? 'cs-btn-primary' : 'cs-btn-outline'}`}
                        onClick={() => triage(o, d)}
                      >
                        {d}
                      </button>
                    ))}
                    {!o.deliverability && isAiConfigured() && (
                      <button
                        type="button"
                        className="cs-btn cs-btn-sm cs-btn-ghost"
                        disabled={busy === `llm-${o.id}`}
                        onClick={() => checkDeliverability(o)}
                      >
                        {busy === `llm-${o.id}` ? 'Screening…' : 'Screen deliverability'}
                      </button>
                    )}
                    <button
                      type="button"
                      className="cs-btn cs-btn-sm cs-btn-primary"
                      disabled={busy === `pursue-${o.id}`}
                      onClick={() => pursue(o)}
                      style={{ marginLeft: 'auto' }}
                    >
                      {o.pursuitDealId ? 'Open pursuit' : busy === `pursue-${o.id}` ? 'Creating…' : 'Pursue →'}
                    </button>
                    {o.link && (
                      <a className="cs-btn cs-btn-sm cs-btn-ghost" href={o.link} target="_blank" rel="noreferrer">
                        sam.gov ↗
                      </a>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
